/**
 * The first question on `/segments/new`: a list of uploads, or a description
 * of who belongs?
 *
 * Two links rather than a toggle, because the two answers are two different
 * forms with different fields — a static segment ticks lists, a rule segment
 * writes rules — and each lives at its own address (`?type=rule`), so the
 * browser's Back goes where a person expects and Overview's "Draft a segment"
 * hand-offs can link straight to either.
 *
 * Each option says what the kind DOES over time, which is the difference that
 * matters after today: a static segment changes only when somebody changes its
 * lists; a rule segment changes by itself as data arrives. A cohort is the
 * third kind and is not offered here — it is saved from an Overview count.
 */
import { Card, cn, focusRing } from '@wend/ui';
import { ListChecks, Sparkles } from 'lucide-react';
import { Link } from 'react-router-dom';

export type SegmentKindChoiceValue = 'static' | 'rule';

const OPTIONS = [
  {
    value: 'static',
    to: '/segments/new',
    title: 'From uploads (static)',
    body: 'Tick the lists to combine. It changes only when you change the lists.',
    Icon: ListChecks,
  },
  {
    value: 'rule',
    to: '/segments/new?type=rule',
    title: 'By rules (dynamic)',
    body: 'Describe who belongs — any column from any upload. Wend keeps it current as data arrives.',
    Icon: Sparkles,
  },
] as const;

export function SegmentKindChoice({ value }: { value: SegmentKindChoiceValue }) {
  return (
    <nav aria-label="Kind of segment">
      <ul className="grid gap-3 sm:grid-cols-2">
        {OPTIONS.map((option) => {
          const current = option.value === value;
          return (
            <li key={option.value}>
              <Card
                className={cn(
                  'h-full transition-colors',
                  current ? 'border-brand-b/55 shadow-selected' : 'hover:border-white/25',
                )}
              >
                <Link
                  to={option.to}
                  replace
                  aria-current={current ? 'true' : undefined}
                  data-kind={option.value}
                  className={cn('flex h-full items-start gap-3 rounded-[inherit] p-4', focusRing)}
                >
                  <option.Icon aria-hidden="true" className={cn('mt-0.5 size-5 shrink-0', current ? 'text-brand-b' : 'text-muted')} />
                  <span className="flex min-w-0 flex-col gap-1">
                    <span className="text-[0.95rem] font-medium text-text">{option.title}</span>
                    <span className="text-[0.84rem] leading-relaxed text-muted">{option.body}</span>
                  </span>
                </Link>
              </Card>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
